import React, { createContext, useContext, useState } from 'react';

type Language = 'en' | 'ru';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

type TranslationKey = string | number;
type TranslationValue = string | { [key: TranslationKey]: TranslationValue };
type Translation = { [key: TranslationKey]: TranslationValue };
type Translations = { [key: string]: Translation };

const translations: Translations = {
  en: {
    app: {
      title: 'Blood Pressure Diary'
    },
    login: {
      title: 'Welcome to Blood Pressure Diary',
      placeholder: 'Enter your patient ID',
      button: 'Login',
      loading: 'Logging in...',
      error: 'Patient ID not found. Please create a new patient first.',
      or: 'or',
      create: 'Create New Patient'
    },
    create: {
      title: 'Create Your Account',
      placeholder: 'Choose a patient ID (numbers only)',
      button: 'Create Account',
      loading: 'Creating your account...',
      error: {
        required: 'Please enter a patient ID',
        failed: 'Failed to create patient. This ID might already be taken.'
      },
      or: 'or',
      back: 'Back to Login'
    },
    main: {
      logout: 'Logout',
      systolic: 'Systolic Pressure:',
      diastolic: 'Diastolic Pressure:',
      pulse: 'Heart Rate:',
      mood: 'How are you feeling?',
      comment: 'Additional Notes:',
      'comment.placeholder': 'Add any relevant notes (e.g., after exercise, before sleep, after medication)',
      add: 'Save Reading',
      adding: 'Saving...',
      readings: 'Your Previous Readings',
      'no.readings': 'No readings yet. Add your first reading!',
      askAi: 'Ask AI!🤖',
      error: {
        required: 'Please fill in all required fields',
        systolic: 'Systolic pressure should be between 70 and 190',
        diastolic: 'Diastolic pressure should be between 40 and 100',
        pulse: 'Heart rate should be between 40 and 200',
        failed: 'Failed to add reading'
      },
      unit: {
        mmhg: 'mmHg',
        bpm: 'bpm'
      }
    },
    ai: {
      title: 'AI Analysis of Your Readings',
      loading: 'Analyzing your readings...',
      error: {
        'no.readings': 'No readings available for analysis',
        failed: 'Failed to get AI analysis. Please try again later.'
      }
    }
  },
  ru: {
    app: {
      title: 'Дневник давления'
    },
    login: {
      title: 'Добро пожаловать в Дневник давления',
      placeholder: 'Введите ваш ID пациента',
      button: 'Войти',
      loading: 'Вход...',
      error: 'ID пациента не найден. Пожалуйста, создайте нового пациента.',
      or: 'или',
      create: 'Создать нового пациента'
    },
    create: {
      title: 'Создать аккаунт',
      placeholder: 'Выберите ID пациента (только цифры)',
      button: 'Создать аккаунт',
      loading: 'Создание аккаунта...',
      error: {
        required: 'Пожалуйста, введите ID пациента',
        failed: 'Не удалось создать пациента. Этот ID может быть уже занят.'
      },
      or: 'или',
      back: 'Вернуться к входу'
    },
    main: {
      logout: 'Выйти',
      systolic: 'Систолическое давление:',
      diastolic: 'Диастолическое давление:',
      pulse: 'Пульс:',
      mood: 'Как вы себя чувствуете?',
      comment: 'Дополнительные заметки:',
      comment_placeholder: 'Добавьте любые важные заметки (например, после тренировки, перед сном, после приема лекарств)',
      add: 'Сохранить показание',
      adding: 'Сохранение...',
      readings: 'Ваши предыдущие показания',
      'no.readings': 'Пока нет показаний. Добавьте первое показание!',
      askAi: 'Спроси ИИ! 🤖',
      error: {
        required: 'Пожалуйста, заполните все обязательные поля',
        systolic: 'Систолическое давление должно быть между 70 и 190',
        diastolic: 'Диастолическое давление должно быть между 40 и 100',
        pulse: 'Пульс должен быть между 40 и 200',
        failed: 'Не удалось добавить показание'
      },
      unit: {
        mmhg: 'мм рт.ст.',
        bpm: 'уд/мин'
      }
    },
    ai: {
      title: 'Анализ ваших показаний от ИИ',
      loading: 'Анализируем ваши показания...',
      error: {
        'no.readings': 'Нет показаний для анализа',
        failed: 'Не удалось получить анализ ИИ. Пожалуйста, попробуйте позже.'
      }
    }
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  const getTranslation = (key: string): string => {
    try {
      const keys = key.split('.');
      let value: any = translations[language];
      
      for (const k of keys) {
        if (!value || typeof value !== 'object') {
          console.warn(`Translation key not found: ${key}`);
          return key;
        }
        value = value[k];
      }
      
      if (typeof value !== 'string') {
        console.warn(`Translation value is not a string: ${key}`);
        return key;
      }
      
      return value;
    } catch (error) {
      console.error(`Error getting translation for key: ${key}`, error);
      return key;
    }
  };

  const t = (key: string): string => {
    return getTranslation(key);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
} 